// Toasts (constitution §5.5: every mutation shows one on success or failure). Errors are announced
// at once (role="alert"); the rest politely (role="status"). Each toast closes itself or by button.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastApi {
  show(kind: ToastKind, text: string): void;
}

interface ToastItem {
  readonly id: number;
  readonly kind: ToastKind;
  readonly text: string;
}

const DEFAULT_DURATION_MS = 6000;
const MAX_VISIBLE = 4;

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider(props: {
  readonly children: ReactNode;
  readonly durationMs?: number;
}): React.JSX.Element {
  const duration = props.durationMs ?? DEFAULT_DURATION_MS;
  const [items, setItems] = useState<readonly ToastItem[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Set<number>());

  const dismiss = useCallback((id: number) => {
    setItems((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (kind: ToastKind, text: string) => {
      nextId.current += 1;
      const id = nextId.current;
      setItems((list) => [...list.slice(1 - MAX_VISIBLE), { id, kind, text }]);
      const timer = window.setTimeout(() => {
        timers.current.delete(timer);
        dismiss(id);
      }, duration);
      timers.current.add(timer);
    },
    [dismiss, duration],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((t) => window.clearTimeout(t));
  }, []);

  const api = useMemo<ToastApi>(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={api}>
      {props.children}
      <div className="toasts" role="region" aria-label="Notifications">
        {items.map((t) => (
          <div
            key={t.id}
            className={`toast toast--${t.kind}`}
            role={t.kind === 'error' ? 'alert' : 'status'}
          >
            <span className="toast__text">{t.text}</span>
            <button
              type="button"
              className="toast__close"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) throw new Error('useToast must be used inside ToastProvider');
  return api;
}
